import { TOPIC_TYPES } from './types';
import type { TopicType, TopicTypeId } from './types';
import type { AppFeatures } from '@/modules/config';
import { conditionalEnabledTopicTypeIds } from '@/conditional_feature';

/**
 * Permanent registry of which topic types are enabled — i.e. offered in the
 * Create/Update flow and shown in the Topics list.
 *
 * The enabled set is the union of a permanent default set and whatever
 * conditional features contribute for the current runtime config. This keeps the
 * consumers (`TopicView`, `Topics`) stable across a flag's whole lifecycle:
 *
 * - Tearing a gating flag down (delete its module + its line in the
 *   `@/conditional_feature` barrel) simply stops contributing its type.
 * - Promoting a gated type to permanent means moving its id into
 *   `DEFAULT_ENABLED_TOPIC_TYPE_IDS` below, then deleting the flag.
 *
 * Neither touches the consumers or the functions here.
 */

// Topic types enabled without any feature flag. To permanently enable a type
// currently gated behind a conditional feature, move its id here.
const DEFAULT_ENABLED_TOPIC_TYPE_IDS: readonly TopicTypeId[] = [
  'beautification',
  'challenge',
  'deal',
  'event',
  'rally',
  'volunteering',
];

function enabledTopicTypeIds(features: AppFeatures): Set<TopicTypeId> {
  return new Set<TopicTypeId>([
    ...DEFAULT_ENABLED_TOPIC_TYPE_IDS,
    ...conditionalEnabledTopicTypeIds(features),
  ]);
}

/** Whether an topic of the given type is enabled for the current config. */
export function isTopicTypeEnabled(
  type: TopicTypeId,
  features: AppFeatures
): boolean {
  return enabledTopicTypeIds(features).has(type);
}

/**
 * Topic types offered in the Create/Update flow, in `TOPIC_TYPES` order,
 * limited to those enabled for the current config.
 */
export function enabledTopicTypes(features: AppFeatures): TopicType[] {
  const ids = enabledTopicTypeIds(features);
  return TOPIC_TYPES.filter(t => ids.has(t.id));
}

/** Narrow a list of topics to those whose type is enabled for the config. */
export function enabledTopics<T extends { type: TopicTypeId }>(
  topics: readonly T[],
  features: AppFeatures
): T[] {
  const ids = enabledTopicTypeIds(features);
  return topics.filter(a => ids.has(a.type));
}
